// Copyright (c) Bentley Systems
import * as React from "react";
import {
  Alert,
  InformationPanelWrapper,
  ProgressRadial,
  Text,
  Tree,
  TreeNode,
} from "@itwin/itwinui-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { selectAccessToken } from "../app/authorization-slice";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import Action from "../Action/Action";
import { ActionParameterVm } from "../Action/action-parameter-vm";
import { ActionVm } from "../Action/action-vm";
import { Parameter } from "../Action/parameter";
import {
  getErrorMessageFromResponse,
  getQueryParam,
  getTranslatedStringFromParameters,
  getUrlBasedOnEnv,
} from "../Helpers/helper-functions";
import IconByName from "../Icons/IconByName";
import { EtNode } from "./DataModels/et-node";
import { toTreeNodeViewModelShallow, TreeNodeData } from "./DataModels/tree-node-data";
import { getBase64IconSrc, TreeNodeDataWithRaw } from "./etree-utils";
import EtreeContextMenu from "./components/EtreeContextMenu";
import EtreeRolloutPanel from "./components/EtreeRolloutPanel";
import EtreeToolbar from "./components/EtreeToolbar";
import { EtreeActionProvider } from "./contexts/EtreeActionContext";
import {
  EtreePersistenceProvider,
  type PersistedEtreeViewState,
  useEtreePersistence,
} from "./contexts/EtreePersistenceContext";
import { useEtreeActionOrchestration } from "./hooks/useEtreeActionOrchestration";
import { useEtreeActions } from "./hooks/useEtreeActions";
import { useEtreeContextMenu } from "./hooks/useEtreeContextMenu";
import { useEtreeData } from "./hooks/useEtreeData";

const ETREE_SEARCH_TERM_REGEX = /^[A-Za-z0-9_-]+(?: +[A-Za-z0-9_-]+)*$/;
const ETREE_SEARCH_TERM_MIN_LENGTH = 3;

// ---------------------------------------------------------------------------
// Static node-renderer style constants (avoid per-render allocations)
// ---------------------------------------------------------------------------
const NODE_ICON_WRAPPER_STYLE: React.CSSProperties = {
  width: "20px",
  height: "20px",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
const NODE_LABEL_ROW_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
};
const NODE_TEXT_COLUMN_STYLE: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
};
const NODE_SUBLABEL_STYLE: React.CSSProperties = {
  fontSize: "0.75rem",
  opacity: 0.65,
  marginLeft: "8px",
  marginTop: "2px",
  lineHeight: 1.2,
};
const NODE_ICON_IMG_STYLE: React.CSSProperties = {
  height: "24px",
  width: "24px",
  objectFit: "contain",
};
const NODE_ICON_SVG_STYLE: React.CSSProperties = {
  width: "20px",
  height: "20px",
  fill: "var(--iui-color-icon)",
};
const NODE_SELECTED_STYLE: React.CSSProperties = {
  backgroundColor: "var(--iui-color-background-accent-muted)",
};
// Per-cursor label outer styles (avoids per-render object creation for the dynamic cursor value)
const NODE_LABEL_OUTER_BASE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  width: "100%",
};
const NODE_LABEL_OUTER_BUSY: React.CSSProperties = { ...NODE_LABEL_OUTER_BASE, cursor: "wait" };
const NODE_LABEL_OUTER_CLICKABLE: React.CSSProperties = { ...NODE_LABEL_OUTER_BASE, cursor: "pointer" };
const NODE_LABEL_OUTER_DEFAULT: React.CSSProperties = { ...NODE_LABEL_OUTER_BASE, cursor: "default" };

const logEtreeDebug = (...args: unknown[]) => {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug("[Etree/Etree]", ...args);
  }
};

const getRouteTreeParams = (pathname: string, routeId: string | undefined, routeParams: string | undefined): string => {
  const directParams = routeParams?.trim() || "";
  if (directParams) return directParams;

  const routeIdValue = routeId?.trim() || "";
  if (!routeIdValue) return "";

  const marker = `/etree/${routeIdValue}/`;
  const markerIndex = pathname.indexOf(marker);
  if (markerIndex < 0) return "";

  const tail = pathname.slice(markerIndex + marker.length).trim();
  return tail;
};

const EtreeContent = () => {
  const { id, params } = useParams();
  const accessToken = useAppSelector(selectAccessToken);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { persistState, loadPersistedState } = useEtreePersistence();
  const iTwinQueryParam = getQueryParam("iTwinId");

  const [treeConfig, setTreeConfig] = React.useState<EtNode | null>(null);
  const [fRollUrl, setFrollUrl] = React.useState<string | undefined>(undefined);
  const [propertyForFrollRefresh, setPropertyForFrollRefresh] = React.useState<boolean>(false);
  const [wasEformChangedAndRefreshed, setwasEformChangedAndRefreshed] = React.useState<boolean>(false);
  const [infoPanelHeader, setInfoPanelHeader] = React.useState<string | undefined>(undefined);
  const [refreshTrigger, setRefreshTrigger] = React.useState<number>(0);
  const [searchQuery, setSearchQuery] = React.useState<string>("");
  const [searchValidationError, setSearchValidationError] = React.useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = React.useState<string | null>(null);

  const treeConfigAbortControllerRef = React.useRef<AbortController | null>(null);
  const treeDataAbortControllerRef = React.useRef<AbortController | null>(null);
  const treeScrollContainerRef = React.useRef<HTMLDivElement | null>(null);
  const nodeElementRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const lastScrollTopRef = React.useRef<number>(0);
  const hasRestoredStateRef = React.useRef<boolean>(false);
  const hasReadPersistedStateRef = React.useRef<boolean>(false);
  const previousTreeContextKeyRef = React.useRef<string>("");
  const previousRouteTreeIdRef = React.useRef<string | undefined>(undefined);
  const currentTreeStateStorageKeyRef = React.useRef<string>("");
  const isHandlingFrollCloseRef = React.useRef<boolean>(false);
  const treeContextKeyRef = React.useRef<string>("");
  const previousPersistenceIdentityRef = React.useRef<{
    storageKey: string;
    routePath: string;
    routeTreeId: string;
    routeTreeParams: string;
  } | null>(null);

  const normalizedRouteTreeParams = React.useMemo(
    () => getRouteTreeParams(location.pathname, id, params),
    [location.pathname, id, params]
  );

  const treeStateStorageKey = React.useMemo(
    () => `etree:view-state:${id || "unknown"}:${normalizedRouteTreeParams || ""}`,
    [id, normalizedRouteTreeParams]
  );
  const treeContextKey = React.useMemo(
    () => `${location.pathname}:${id || "unknown"}:${normalizedRouteTreeParams}`,
    [location.pathname, id, normalizedRouteTreeParams]
  );

  React.useEffect(() => {
    // Prevent stale treeConfig (previous route) from driving refresh calls.
    setTreeConfig(null);
  }, [treeContextKey]);

  React.useEffect(() => {
    treeContextKeyRef.current = treeContextKey;
  }, [treeContextKey]);

  React.useEffect(() => {
    currentTreeStateStorageKeyRef.current = treeStateStorageKey;
  }, [treeStateStorageKey]);

  const isValidEtreeSearchTerm = React.useCallback((value: string): boolean => {
    const trimmedValue = value.trim();
    return (
      trimmedValue.length >= ETREE_SEARCH_TERM_MIN_LENGTH &&
      ETREE_SEARCH_TERM_REGEX.test(trimmedValue)
    );
  }, []);

  // -------------------------------------------------------------------------
  // Hooks
  // -------------------------------------------------------------------------

  const {
    headerActions,
    headerBackAction,
    itemActionsMap,
    buildHeaderActions,
    buildItemActions,
  } = useEtreeActions(treeConfig);

  const {
    treeData,
    isLoading,
    error,
    expandedNodes,
    loadingNodes,
    appliedSearchTerm,
    urlParameters,
    policyExpandedCandidates,
    manualExpanded,
    manualCollapsed,
    refreshTree,
    restoreExpandedNodes,
    restoreExpansionIntent,
    setError,
    setAppliedSearchTerm,
    fetchTreeData,
    onNodeExpanded,
    getNode,
  } = useEtreeData({
    treeConfig,
    accessToken,
    urlParams: params,
    iTwinQueryParam,
    treeContextKey,
    itemActionsMap,
  });

  const buildHeaderActionsRef = React.useRef(buildHeaderActions);
  const buildItemActionsRef = React.useRef(buildItemActions);
  const fetchTreeDataRef = React.useRef(fetchTreeData);
  const setErrorRef = React.useRef(setError);
  const selectedNodeIdRef = React.useRef<string | null>(selectedNodeId);
  const expandedNodesRef = React.useRef<Record<string, boolean>>(expandedNodes);
  const searchQueryRef = React.useRef<string>(searchQuery);
  const policyExpandedCandidatesRef = React.useRef<Record<string, boolean>>(policyExpandedCandidates);
  const manualExpandedRef = React.useRef<Record<string, boolean>>(manualExpanded);
  const manualCollapsedRef = React.useRef<Record<string, boolean>>(manualCollapsed);

  React.useEffect(() => {
    buildHeaderActionsRef.current = buildHeaderActions;
    buildItemActionsRef.current = buildItemActions;
    fetchTreeDataRef.current = fetchTreeData;
    setErrorRef.current = setError;
  }, [buildHeaderActions, buildItemActions, fetchTreeData, setError]);

  React.useEffect(() => {
    selectedNodeIdRef.current = selectedNodeId;
  }, [selectedNodeId]);

  React.useEffect(() => {
    expandedNodesRef.current = expandedNodes;
  }, [expandedNodes]);

  React.useEffect(() => {
    searchQueryRef.current = searchQuery;
  }, [searchQuery]);

  React.useEffect(() => {
    policyExpandedCandidatesRef.current = policyExpandedCandidates;
  }, [policyExpandedCandidates]);

  React.useEffect(() => {
    manualExpandedRef.current = manualExpanded;
  }, [manualExpanded]);

  React.useEffect(() => {
    manualCollapsedRef.current = manualCollapsed;
  }, [manualCollapsed]);

  React.useEffect(() => {
    if (fRollUrl !== undefined) return;
    if (!selectedNodeId) return;

    requestAnimationFrame(() => {
      const selectedNodeElement = nodeElementRefs.current[selectedNodeId];
      if (!selectedNodeElement || !treeScrollContainerRef.current) return;

      selectedNodeElement.scrollIntoView({
        block: "nearest",
        inline: "nearest",
        behavior: "auto",
      });
    });
  }, [selectedNodeId, treeData, expandedNodes, fRollUrl]);

  const saveTreeViewState = React.useCallback(
    (override?: Partial<PersistedEtreeViewState>, reason = "unspecified") => {
      void reason;
      const stateToPersist: PersistedEtreeViewState = {
        routePath: location.pathname,
        routeTreeId: id || "",
        routeTreeParams: normalizedRouteTreeParams,
        selectedNodeId: selectedNodeIdRef.current,
        expandedNodes: expandedNodesRef.current,
        appliedSearchTerm: appliedSearchTermRef.current,
        searchQuery: searchQueryRef.current || appliedSearchTermRef.current,
        scrollTop: lastScrollTopRef.current,
        policyExpandedCandidates: policyExpandedCandidatesRef.current,
        manualExpanded: manualExpandedRef.current,
        manualCollapsed: manualCollapsedRef.current,
        ...override,
      };
      persistState(treeStateStorageKey, stateToPersist);
    },
    [treeStateStorageKey, location.pathname, id, normalizedRouteTreeParams, persistState]
  );

  const saveTreeViewStateRef = React.useRef(saveTreeViewState);
  React.useEffect(() => {
    saveTreeViewStateRef.current = saveTreeViewState;
  }, [saveTreeViewState]);
  const saveTreeViewStateStable = React.useCallback(
    (...args: Parameters<typeof saveTreeViewState>) => saveTreeViewStateRef.current(...args),
    []
  );

  // -------------------------------------------------------------------------
  // Tree config fetch
  // -------------------------------------------------------------------------

  const appliedSearchTermRef = React.useRef<string>("");
  React.useEffect(() => {
    appliedSearchTermRef.current = appliedSearchTerm;
  }, [appliedSearchTerm]);

  React.useEffect(() => {
    const previousPersistenceIdentity = previousPersistenceIdentityRef.current;

    if (
      previousPersistenceIdentity &&
      previousPersistenceIdentity.storageKey !== treeStateStorageKey
    ) {
      const previousStateToPersist: PersistedEtreeViewState = {
        routePath: previousPersistenceIdentity.routePath,
        routeTreeId: previousPersistenceIdentity.routeTreeId,
        routeTreeParams: previousPersistenceIdentity.routeTreeParams,
        selectedNodeId: selectedNodeIdRef.current,
        expandedNodes: expandedNodesRef.current,
        appliedSearchTerm: appliedSearchTermRef.current,
        searchQuery: searchQueryRef.current || appliedSearchTermRef.current,
        scrollTop: lastScrollTopRef.current,
        policyExpandedCandidates: policyExpandedCandidatesRef.current,
        manualExpanded: manualExpandedRef.current,
        manualCollapsed: manualCollapsedRef.current,
      };

      logEtreeDebug("persist previous tree state before key switch", {
        previousStorageKey: previousPersistenceIdentity.storageKey,
        appliedSearchTerm: previousStateToPersist.appliedSearchTerm,
        searchQuery: previousStateToPersist.searchQuery,
      });

      persistState(previousPersistenceIdentity.storageKey, previousStateToPersist);
    }

    hasRestoredStateRef.current = false;
    hasReadPersistedStateRef.current = false;

    logEtreeDebug("treeStateStorageKey changed", {
      treeStateStorageKey,
      routeTreeId: id,
      normalizedRouteTreeParams,
    });

    appliedSearchTermRef.current = "";
    setAppliedSearchTerm("");

    searchQueryRef.current = "";
    setSearchQuery("");
    setSearchValidationError(null);

    previousPersistenceIdentityRef.current = {
      storageKey: treeStateStorageKey,
      routePath: location.pathname,
      routeTreeId: id || "",
      routeTreeParams: normalizedRouteTreeParams,
    };
  }, [treeStateStorageKey, location.pathname, id, normalizedRouteTreeParams, persistState, setAppliedSearchTerm]);

  React.useEffect(() => {
    if (!hasReadPersistedStateRef.current) return;
    if (!hasRestoredStateRef.current) return;
    saveTreeViewState(undefined, "post_restore_effect");
  }, [saveTreeViewState]);

  React.useEffect(() => {
    if (!hasReadPersistedStateRef.current) return;
    if (!hasRestoredStateRef.current) return;
    saveTreeViewState(undefined, "state_change_effect");
  }, [
    saveTreeViewState,
    selectedNodeId,
    expandedNodes,
    appliedSearchTerm,
    searchQuery,
    policyExpandedCandidates,
    manualExpanded,
    manualCollapsed,
    normalizedRouteTreeParams,
  ]);

  React.useEffect(() => {
    const treeScrollContainer = treeScrollContainerRef.current;
    const cleanupStorageKey = treeStateStorageKey;

    return () => {
      if (currentTreeStateStorageKeyRef.current !== cleanupStorageKey) {
        logEtreeDebug("skip unmount cleanup save on key switch", {
          cleanupStorageKey,
          currentStorageKey: currentTreeStateStorageKeyRef.current,
        });
        return;
      }

      const currentScrollTop = treeScrollContainer?.scrollTop ?? lastScrollTopRef.current;

      if (!hasReadPersistedStateRef.current) {
        const hasMeaningfulState =
          selectedNodeIdRef.current !== null ||
          Object.values(expandedNodesRef.current).some((isExpanded) => isExpanded) ||
          Boolean(appliedSearchTermRef.current.trim()) ||
          Boolean(searchQueryRef.current.trim()) ||
          currentScrollTop > 0;

        if (!hasMeaningfulState) {
          return;
        }
      }

      saveTreeViewState({ scrollTop: currentScrollTop }, "unmount_cleanup");
    };
  }, [saveTreeViewState, treeStateStorageKey]);

  React.useEffect(() => {
    treeConfigAbortControllerRef.current?.abort();
    const treeConfigAbortController = new AbortController();
    treeConfigAbortControllerRef.current = treeConfigAbortController;

    const fetchTreeConfiguration = async () => {
      if (!id) {
        setErrorRef.current("No tree node ID provided");
        return;
      }

      try {
        const url = getUrlBasedOnEnv(`/api/Tree/GetTreeConfiguration/${encodeURIComponent(id)}`);
        treeDataAbortControllerRef.current?.abort();
        const treeDataAbortController = new AbortController();
        treeDataAbortControllerRef.current = treeDataAbortController;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: crypto.randomUUID(),
            ...(iTwinQueryParam && { "Itwin-ID": iTwinQueryParam }),
          },
          signal: treeConfigAbortController.signal,
        });

        if (!response.ok) {
          const message = await getErrorMessageFromResponse(response);
          setErrorRef.current(message);
          return;
        }

        const config: EtNode = await response.json();

        setTreeConfig(config);
        buildHeaderActionsRef.current(config);
        const actionsMap = buildItemActionsRef.current(config);

        // Use current applied search term so re-fires after restore don't wipe filtered data.
        const configInitialSearchTerm = appliedSearchTermRef.current;
        const preserveExpandedStateOnConfigLoad =
          hasRestoredStateRef.current || hasReadPersistedStateRef.current;
        logEtreeDebug("initial tree load", {
          routeTreeId: id,
          normalizedRouteTreeParams,
          configInitialSearchTerm,
          preserveExpandedStateOnConfigLoad,
        });

        await fetchTreeDataRef.current(
          config,
          undefined,
          preserveExpandedStateOnConfigLoad,
          actionsMap,
          treeDataAbortControllerRef.current?.signal,
          { searchTermOverride: configInitialSearchTerm }
        );
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setErrorRef.current(err instanceof Error ? err.message : "Failed to load tree configuration");
      }
    };

    void fetchTreeConfiguration();

    return () => {
      const treeDataAbortController = treeDataAbortControllerRef.current;
      treeDataAbortController?.abort();
      if (treeDataAbortControllerRef.current === treeDataAbortController) {
        treeDataAbortControllerRef.current = null;
      }

      treeConfigAbortController.abort();
      if (treeConfigAbortControllerRef.current === treeConfigAbortController) {
        treeConfigAbortControllerRef.current = null;
      }
    };
  }, [id, accessToken, iTwinQueryParam, normalizedRouteTreeParams]);

  // Refresh on trigger (e.g. rollout close)
  React.useEffect(() => {
    if (refreshTrigger > 0) {
      const isCurrentTreeConfig = !!treeConfig && !!id && String(treeConfig.nodeId) === id;
      if (!isCurrentTreeConfig) return;
      void refreshTree(undefined, 'panel-close');
    }
  }, [refreshTrigger, normalizedRouteTreeParams, refreshTree, treeConfig, id]);

  React.useEffect(() => {
    const previousContextKey = previousTreeContextKeyRef.current;
    const previousRouteTreeId = previousRouteTreeIdRef.current;
    const contextChanged =
      previousContextKey !== "" && previousContextKey !== treeContextKey;
    const sameRouteTreeId = previousRouteTreeId !== undefined && previousRouteTreeId === id;
    const isCurrentTreeConfig =
      !!treeConfig && !!id && String(treeConfig.nodeId) === id;

    previousTreeContextKeyRef.current = treeContextKey;
    previousRouteTreeIdRef.current = id;

    if (contextChanged && sameRouteTreeId && isCurrentTreeConfig) {
      void refreshTree(undefined, 'action');
    }
  }, [treeContextKey, id, treeConfig, refreshTree]);

  React.useEffect(() => {
    if (!treeConfig || isLoading || hasRestoredStateRef.current) return;

    const isCurrentTreeConfig = !!id && String(treeConfig.nodeId) === id;
    if (!isCurrentTreeConfig) return;

    const persistedState = loadPersistedState(treeStateStorageKey);
    hasReadPersistedStateRef.current = true;

    const currentRouteTreeId = id || "";
    const currentRouteTreeParams = normalizedRouteTreeParams;

    const hasMatchingPersistedIdentity = (() => {
      if (!persistedState) return false;

      const sameTreeId = persistedState.routeTreeId === currentRouteTreeId;
      if (!sameTreeId) return false;

      return persistedState.routeTreeParams === currentRouteTreeParams;
    })();

    logEtreeDebug("restore evaluation", {
      treeStateStorageKey,
      hasPersistedState: Boolean(persistedState),
      hasMatchingPersistedIdentity,
      persistedSearch: persistedState?.appliedSearchTerm,
      currentRouteTreeId,
      currentRouteTreeParams,
    });

    if (!persistedState || !hasMatchingPersistedIdentity) {
      hasRestoredStateRef.current = true;
      return;
    }

    const restoreState = async () => {
      // Set immediately to prevent re-entry if isLoading toggles while we await refreshTree.
      hasRestoredStateRef.current = true;
      const restoreStorageKey = treeStateStorageKey;
      const isStaleRestore = () => currentTreeStateStorageKeyRef.current !== restoreStorageKey;

      if (isStaleRestore()) return;

      const restoredSearchTerm = persistedState?.appliedSearchTerm?.trim() || "";
      const restoredSearchQuery = (persistedState?.searchQuery || "").trim() || restoredSearchTerm;
      searchQueryRef.current = restoredSearchQuery;
      setSearchQuery(restoredSearchQuery);

      const restoredScrollTop = Math.max(0, persistedState?.scrollTop || 0);
      lastScrollTopRef.current = restoredScrollTop;

      logEtreeDebug("restoreState apply", {
        restoredSearchQuery,
        restoredSearchTerm,
        restoredSelectedNodeId: persistedState?.selectedNodeId || null,
      });

      appliedSearchTermRef.current = restoredSearchTerm;
      setAppliedSearchTerm(restoredSearchTerm);
      if (restoredSearchTerm) {
        restoreExpandedNodes({});
        restoreExpansionIntent({}, {}, {});
        await refreshTree(restoredSearchTerm, 'search');
        if (isStaleRestore()) return;
      } else {
        const restoredExpandedNodes = persistedState?.expandedNodes || {};
        const hasExpandedNodes = Object.values(restoredExpandedNodes).some((isExpanded) => isExpanded);
        const restoredPolicy = persistedState?.policyExpandedCandidates || {};
        const restoredManualExpanded = persistedState?.manualExpanded || {};
        const restoredManualCollapsed = persistedState?.manualCollapsed || {};

        if (hasExpandedNodes) {
          restoreExpandedNodes(restoredExpandedNodes);
        }
        restoreExpansionIntent(restoredPolicy, restoredManualExpanded, restoredManualCollapsed);

        if (!hasExpandedNodes) {
          restoreExpandedNodes({});
          restoreExpansionIntent({}, {}, {});
        }

        if (hasExpandedNodes) {
        await refreshTree(undefined, 'panel-close');
        if (isStaleRestore()) return;
          restoreExpandedNodes(restoredExpandedNodes);
        }
      }

      if (isStaleRestore()) return;

      const restoredSelectedNodeId = persistedState?.selectedNodeId || null;
      selectedNodeIdRef.current = restoredSelectedNodeId;
      setSelectedNodeId(restoredSelectedNodeId);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (isStaleRestore()) return;
          if (treeScrollContainerRef.current) {
            treeScrollContainerRef.current.scrollTop = restoredScrollTop;
          }
        });
      });
    };

    void restoreState();
  }, [
    treeConfig,
    isLoading,
    treeStateStorageKey,
    id,
    normalizedRouteTreeParams,
    setAppliedSearchTerm,
    refreshTree,
    restoreExpandedNodes,
    restoreExpansionIntent,
    loadPersistedState,
  ]);

  // -------------------------------------------------------------------------
  // Header / title
  // -------------------------------------------------------------------------

  const header = React.useMemo(
    () =>
      treeConfig?.caption
        ? getTranslatedStringFromParameters(treeConfig.caption, urlParameters)
        : "",
    [treeConfig, urlParameters]
  );

  // -------------------------------------------------------------------------
  // Rollout panel helpers
  // -------------------------------------------------------------------------

  const navigateToInfPanelFunction = React.useCallback(
    (navFrolUrl: string) => {
      isHandlingFrollCloseRef.current = false;
      setInfoPanelHeader(undefined);
      if (fRollUrl === navFrolUrl) {
        setPropertyForFrollRefresh(!propertyForFrollRefresh);
        setwasEformChangedAndRefreshed(true);
      }
      setFrollUrl(navFrolUrl);
    },
    [fRollUrl, propertyForFrollRefresh]
  );

  const onFrollClose = React.useCallback((shouldRefreshTree: boolean) => {
    if (isHandlingFrollCloseRef.current) {
      return;
    }
    isHandlingFrollCloseRef.current = true;

    setFrollUrl(undefined);
    setwasEformChangedAndRefreshed(false);
    setInfoPanelHeader(undefined);
    if (shouldRefreshTree) {
      setRefreshTrigger((prev) => prev + 1);
    }
  }, []);

  const onSetInfoPanelHeader = React.useCallback((h: string) => {
    setInfoPanelHeader(h);
  }, []);

  // -------------------------------------------------------------------------
  // Context menu
  // -------------------------------------------------------------------------

  const handleContextMenuNodeSelected = React.useCallback((nodeId: string) => {
    selectedNodeIdRef.current = nodeId;
    setSelectedNodeId(nodeId);
  }, []); // selectedNodeIdRef is a stable ref; setSelectedNodeId is a stable dispatch

  const { contextMenu, contextMenuRef, closeContextMenu, onNodeContextMenu } =
    useEtreeContextMenu({ onNodeSelected: handleContextMenuNodeSelected });

  // -------------------------------------------------------------------------
  // Action execution
  // -------------------------------------------------------------------------

  const { actionPendingNodes, executeNodeAction } = useEtreeActionOrchestration({
    itemActionsMap,
    urlParameters,
    accessToken,
    navigateToInfPanelFunction,
    navigate,
    dispatch,
    selectedNodeIdRef,
    expandedNodesRef,
    setSelectedNodeId,
    saveTreeViewState: saveTreeViewStateStable,
    treeContextKeyRef,
    refreshTree,
    onNodeExpanded,
  });

  // -------------------------------------------------------------------------
  // Node renderer
  // -------------------------------------------------------------------------

  const renderNode = React.useCallback(
    ({ node, ...rest }: { node: TreeNodeDataWithRaw }) => {
      const viewNode = toTreeNodeViewModelShallow(node, {
        expandedNodeIds: expandedNodes,
        loadingNodeIds: loadingNodes,
        selectedNodeId,
      });
      const isActionPending = actionPendingNodes[node.id] === true;
      const isNodeBusy = isActionPending || viewNode.flags.isLoading === true;
      const isSelected = viewNode.flags.isSelected === true;
      const hasActions = node.actionIds && node.actionIds.length > 0;

      const outerStyle = isNodeBusy
        ? NODE_LABEL_OUTER_BUSY
        : hasActions
          ? NODE_LABEL_OUTER_CLICKABLE
          : NODE_LABEL_OUTER_DEFAULT;

      const labelContent = (
        <div
          ref={(element) => {
            nodeElementRefs.current[node.id] = element;
          }}
          onClick={(e) => {
            e.stopPropagation();
            selectedNodeIdRef.current = node.id;
            setSelectedNodeId(node.id);
            if (!isNodeBusy && hasActions) {
              executeNodeAction(node, viewNode.flags.isExpanded === true);
            }
          }}
          onContextMenu={(e) => {
            e.stopPropagation();
            onNodeContextMenu(node.id, e);
          }}
          style={outerStyle}
        >
          {(viewNode.icon || viewNode.iconBase64Str) && (
            <div style={NODE_ICON_WRAPPER_STYLE}>
              {viewNode.iconBase64Str ? (
                <img
                  src={getBase64IconSrc(viewNode.iconBase64Str)}
                  alt=""
                  style={NODE_ICON_IMG_STYLE}
                />
              ) : (
                viewNode.icon && (
                  <IconByName
                    name={viewNode.icon}
                    style={NODE_ICON_SVG_STYLE}
                  />
                )
              )}
            </div>
          )}
          <div style={NODE_LABEL_ROW_STYLE}>
            <div style={NODE_TEXT_COLUMN_STYLE}>
              <span>{viewNode.label}</span>
              {viewNode.sublabel && (
                <span style={NODE_SUBLABEL_STYLE}>
                  {viewNode.sublabel}
                </span>
              )}
            </div>
            {isNodeBusy && (
              <div style={NODE_ICON_WRAPPER_STYLE}>
                <ProgressRadial size="x-small" indeterminate />
              </div>
            )}
          </div>
        </div>
      );

      return (
        <TreeNode
          nodeId={node.id}
          label={labelContent}
          onExpanded={onNodeExpanded}
          onKeyDown={(e: React.KeyboardEvent) => {
            if ((e.key === "Enter" || e.key === " ") && !isNodeBusy && hasActions) {
              e.preventDefault();
              e.stopPropagation();
              executeNodeAction(node, viewNode.flags.isExpanded === true);
            }
          }}
          style={isSelected ? NODE_SELECTED_STYLE : undefined}
          {...rest}
        />
      );
    },
    [onNodeExpanded, onNodeContextMenu, executeNodeAction, actionPendingNodes, expandedNodes, loadingNodes, selectedNodeId]
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <ProgressRadial indeterminate />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ margin: "16px" }}>
        <Alert type="negative">
          {error}
          <Alert.CloseButton onClick={() => setError(null)} />
        </Alert>
      </div>
    );
  }

  if (!treeConfig) {
    return (
      <div style={{ margin: "16px" }}>
        <Alert type="informational">No tree data available</Alert>
      </div>
    );
  }

  const resolveActionParametersFromUrl = (
    sourceParams: ActionParameterVm[]
  ): ActionParameterVm[] => {
    return sourceParams.map((p) => {
      const param = Object.assign(new ActionParameterVm(), p);
      if (param.valueType === "P") {
        const fieldName = param.value || param.name;
        if (fieldName) {
          const urlParam = urlParameters.find(
            (up: Parameter) => up.name?.toLocaleLowerCase() === fieldName.toLocaleLowerCase()
          );
          if (urlParam) param.value = urlParam.value;
        }
      }
      return param;
    });
  };

  return (
    <div style={{ height: "100%" }}>
      <InformationPanelWrapper
        style={{ height: "100%", maxWidth: "100%", borderRadius: 0 }}
      >
        <div>
          {fRollUrl !== undefined && <div className="overlay" />}
          <div
            style={
              fRollUrl !== undefined
                ? { pointerEvents: "none", opacity: "0.8" }
                : {}
            }
          >
          {/* Header */}
          {treeConfig.tableName && (
            <div className="becs-infopanelbody-footer-line">
              <div className="becs-all-margin">
                <div className="rowWrap">
                  {headerBackAction &&
                    (() => {
                      const processedAction = new ActionVm(headerBackAction);
                      processedAction.actionParameters = resolveActionParametersFromUrl(
                        headerBackAction.actionParameters
                      );
                      return (
                        <Action
                          action={processedAction}
                          key={headerBackAction.actionId}
                          navigateToInformationPanelFunction={navigateToInfPanelFunction}
                        />
                      );
                    })()}
                  <Text variant="title">{header}</Text>
                </div>
              </div>
            </div>
          )}

          {/* Action buttons + search */}
          <EtreeToolbar
            headerActions={headerActions}
            searchQuery={searchQuery}
            searchValidationError={searchValidationError}
            setSearchQuery={(value) => {
              const nextValue =
                typeof value === "function" ? value(searchQueryRef.current) : value;
              searchQueryRef.current = nextValue;
              setSearchQuery(nextValue);
            }}
            setSearchValidationError={setSearchValidationError}
            isValidEtreeSearchTerm={isValidEtreeSearchTerm}
            appliedSearchTermRef={appliedSearchTermRef}
            setAppliedSearchTerm={setAppliedSearchTerm}
            restoreExpandedNodes={restoreExpandedNodes}
            restoreExpansionIntent={restoreExpansionIntent}
            refreshTree={refreshTree}
            resolveActionParametersFromUrl={resolveActionParametersFromUrl}
            navigateToInfPanelFunction={navigateToInfPanelFunction}
          />

          <div className="becs-ltr-margin">
            <div
              style={{
                width: "100%",
              }}
            >
              {/* Tree */}
              <div
                ref={treeScrollContainerRef}
                onScroll={(event) => {
                  const target = event.currentTarget;
                  lastScrollTopRef.current = target.scrollTop;

                  if (!hasReadPersistedStateRef.current || !hasRestoredStateRef.current) {
                    return;
                  }

                  saveTreeViewState({ scrollTop: target.scrollTop }, "scroll");
                }}
                style={{
                  overflow: "auto",
                  minHeight: 0,
                  height: "calc(100vh - 220px)",
                  paddingRight: "0px",
                }}
              >
                {treeData.length === 0 ? (
                  <div style={{ margin: "16px" }}>
                    <Alert type="informational">No tree data available</Alert>
                  </div>
                ) : (
                  <Tree<TreeNodeData>
                    data={treeData}
                    getNode={getNode}
                    nodeRenderer={renderNode}
                  />
                )}
              </div>
            </div>

          {/* Context menu */}
          <EtreeContextMenu
            contextMenu={contextMenu}
            treeData={treeData}
            itemActionsMap={itemActionsMap}
            expandedNodes={expandedNodes}
            executeNodeAction={executeNodeAction}
            closeContextMenu={closeContextMenu}
            contextMenuRef={contextMenuRef}
          />
            </div>
          </div>
        </div>

        {/* Rollout panel */}
        <EtreeRolloutPanel
          fRollUrl={fRollUrl}
          infoPanelHeader={infoPanelHeader}
          onFrollClose={onFrollClose}
          navigateToInfPanelFunction={navigateToInfPanelFunction}
          onSetInfoPanelHeader={onSetInfoPanelHeader}
          propertyForFrollRefresh={propertyForFrollRefresh}
          wasEformChangedAndRefreshed={wasEformChangedAndRefreshed}
        />
      </InformationPanelWrapper>
    </div>
  );
};

const Etree = () => {
  return (
    <EtreePersistenceProvider>
      <EtreeActionProvider>
        <EtreeContent />
      </EtreeActionProvider>
    </EtreePersistenceProvider>
  );
};

export default Etree;
